import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../providers/UserProvider';
import { listBetaSearches, type BetaSearch } from '../repositories/betaSearchRepository';
import { extractErrorMessage } from '../api';
import { getAnonymousInterestsCookie, reconcileAnonymousInterests } from '../utils/anonymousInterestsCookie';
import { useReaderRoutes } from '../providers/ReaderRoutesProvider';
import { feedIdFromPath, filterFeedsForSwitcher, isCurrentFeed, readerFeedList } from '../utils/feedSwitcher';
import { isFeedSwitcherShortcut } from '../utils/keyboard';
import { isModalOpen } from './ModalOverlay';
import { IconSelector } from './icons';

/**
 * The button after the header's "TLDR" brand: the current feed's name (none on
 * Home) and a selector glyph. It opens a filterable, alphabetical list of every
 * feed the reader has, in Home or not. `f` opens it from anywhere on the page.
 */
export function FeedSwitcher({ name }: { name?: string }) {
  const { isLoggedIn } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const routes = useReaderRoutes();
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [feeds, setFeeds] = useState<BetaSearch[] | null>(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  // Load on each open so a feed added or switched elsewhere shows up.
  useEffect(() => {
    if (!open) return;
    let disposed = false;
    setError('');
    void listBetaSearches().then(data => {
      if (disposed) return;
      const searches = isLoggedIn ? data.searches : reconcileAnonymousInterests(data.searches, data.public_searches, getAnonymousInterestsCookie());
      setFeeds(readerFeedList(searches, data.public_searches));
    }).catch(err => { if (!disposed) setError(extractErrorMessage(err)); });
    return () => { disposed = true; };
  }, [open, isLoggedIn]);
  useEffect(() => {
    if (!open) return;
    const onPress = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', onPress);
    return () => document.removeEventListener('pointerdown', onPress);
  }, [open]);
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const typing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (!isFeedSwitcherShortcut(event, typing) || isModalOpen()) return;
      event.preventDefault();
      setQuery(''); setActive(0); setOpen(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const currentId = feedIdFromPath(location.pathname);
  const shown = filterFeedsForSwitcher(feeds || [], query);
  function toggle() {
    setQuery(''); setActive(0); setOpen(value => !value);
  }
  function close() {
    setOpen(false); button.current?.focus();
  }
  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive(index => Math.min(index + 1, shown.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive(index => Math.max(index - 1, 0)); }
    else if (event.key === 'Enter' && shown[active]) { event.preventDefault(); navigate(routes.feed(shown[active].id)); }
    else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
  }
  return (
    <div className="feed-switcher" ref={root}>
      <button
        ref={button}
        type="button"
        className="beta-reader-brand feed-switcher-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={name ? `${name} — switch feed` : 'Switch feed'}
        title={name ? `${name} — switch feed (f)` : 'Switch feed (f)'}
        onClick={toggle}
      >
        {name && <span className="feed-switcher-label">{name}</span>}
        <IconSelector />
      </button>
      {open && <div className="reader-popover feed-switcher-popover">
        <input
          className="feed-switcher-filter"
          type="text"
          autoFocus
          placeholder="Filter feeds"
          aria-label="Filter feeds"
          aria-controls={listId}
          aria-activedescendant={shown[active] ? `${listId}-${shown[active].id}` : undefined}
          value={query}
          onChange={event => { setQuery(event.target.value); setActive(0); }}
          onKeyDown={onKeyDown}
        />
        {error && <p role="alert" className="feed-switcher-note">{error}</p>}
        {!error && !feeds && <p role="status" className="feed-switcher-note">Loading feeds…</p>}
        {feeds && shown.length === 0 && <p className="feed-switcher-note">No feeds match “{query.trim()}”</p>}
        {shown.length > 0 && <ul id={listId} role="listbox" aria-label="Your feeds" className="feed-switcher-list">
          {shown.map((feed, index) => {
            const current = isCurrentFeed(feed, currentId);
            return <li key={feed.id} id={`${listId}-${feed.id}`} role="option" aria-selected={index === active}>
              <Link
                to={routes.feed(feed.id)}
                className={`feed-switcher-item${index === active ? ' active' : ''}${current ? ' current' : ''}${feed.included ? '' : ' inactive'}`}
                aria-current={current ? 'page' : undefined}
                tabIndex={-1}
                onMouseEnter={() => setActive(index)}
              >
                <span className="feed-switcher-name">{feed.name}</span>
                {feed.included && <span className="feed-switcher-badge">In Home</span>}
              </Link>
            </li>;
          })}
        </ul>}
        <div className="feed-switcher-footer"><Link to={routes.home}>Home</Link><Link to={routes.feeds}>Manage feeds</Link></div>
      </div>}
    </div>
  );
}
